import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Fuse from "fuse.js";
import JobTable from './Components/JobTable';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  OutlinedInput,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  Paper,
  DialogContentText,
  Grid
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import axios from 'axios';
import { useRobotTask, useTaskReportMutation } from '../../hooks/robotTask';
import { useRobots } from '../../hooks/robot';
import { useLocationPoints } from '../../hooks/location';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { GridSearchIcon } from '@mui/x-data-grid';
import csv from '../../assets/csv.png';
import { RobotTaskResponse, TaskReportResponse } from '../../hooks/robotTask/index.type';
import { useNavigate } from 'react-router-dom';
import { COLOR } from '../../utils/colors';
import html2canvas from 'html2canvas';
import { TimeNumberProps } from './Components/_type';
import jsPDF from 'jspdf';
import { DEFAULT_DATEFORMAT } from '../scheduledJob/_function/common';
import JobFilterDialog from './Components/JobFilter';
import { parseAsInteger, useQueryState } from 'nuqs';
import { customLog, underscoreToSpace } from '../../utils/utils';
import {
  useAnalyticsCard,
  useCreateAnalyticsCardMutation,
  useDeleteAnalyticsCardMutation,
  useEditAnalyticsCardMutation,
  useShowAnalytics,
  useUpdateAnalyticsLayoutMutation
} from '../../hooks/analytics';
import GridLayout, { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import NewCustomAnalyticDialog, { CardType, CustomCardProps } from './Components/NewCustomAnalytic';
import CustomCard from './Components/CustomCard';
import { Switch } from '../../components/Switch';
import { DYNAMIC_WIDGET_ENABLED } from '../../utils/const';
import DPRate from './Components/DPRate';
import DPAverageTime from './Components/DPAverageTime';
import LiftSuccessRate from './Components/LiftSuccessRate';
import FirstAttemptLiftRate from './Components/AttemptLiftRate';
import DPDeliveryChart from './Components/DPDeliveryChart';
import { useDashboardMode } from '../../hooks/appMode';
import { useAuth } from '../../context/AuthContext';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface columnType {
  id: string;
  label: string;
  padding?: string;
}

type TimeOptionType = {
  name: string,
  value: { time: number, unit: dayjs.ManipulateType }
};

type CleaningTaskItem = {
  task_id: number;
  created_time: string;
  start_time: string;
  robot_id: string;
  status: string;
  organisation_id: string;
  description: string;
  task_name: string;
  percentage: number;
  estimated_time_remaining: number;
  areas: string[];
  cleaning_type: string;
  estimated_time_required: number;
  job_number: string;
  completed_time: string;
  last_updated_time: string;
  cleaned_area: number;
  expected_cleaned_area: number;
};

type CleaningTaskResponse = {
  count: number;
  num_pages: number;
  current_page: number;
  data: CleaningTaskItem[];
};

const timeOptions: TimeOptionType[] = [
  { name: "Yesterday", value: { time: 1, unit: 'days' } },
  { name: "Today", value: { time: 0, unit: 'days' } },
  { name: "Past 3 days", value: { time: 3, unit: 'days' } },
  { name: "Past 7 days", value: { time: 7, unit: 'days' } },
  { name: "Past 2 weeks", value: { time: 14, unit: 'days' } },
  { name: "Custom", value: { time: 14, unit: 'days' } }
];

const defaultTime: TimeOptionType = {
  name: "Past 7 days",
  value: { time: 7, unit: 'days' }
};

const deliveryTasksColumn: columnType[] = [
  { id: 'job_id', label: 'Job No.', padding: "15px 20px" },
  { id: 'robot_name', label: 'Robot Name', padding: "15px 20px" },
  { id: 'from_location', label: 'Route' },
  { id: 'orders', label: 'Orders' },
  { id: 'status', label: 'Status' },
  { id: 'duration', label: 'Total Duration' },
  { id: 'number_failed_deliveries', label: 'Failed Orders' },
  { id: 'error', label: 'Job Error' },
  { id: 'created_at', label: 'Start Time' },
  { id: 'return_time_start', label: 'Delivery Completed' },
  { id: 'return_time_completed', label: 'Job Completed' },
];

const cleaningTasksColumn: columnType[] = [
  { id: 'job_number', label: 'Job No.', padding: "15px 20px" },
  { id: 'task_name', label: 'Task Name', padding: "15px 20px" },
  { id: 'robot_id', label: 'Robot ID', padding: "15px 20px" },
  { id: 'areas', label: 'Area(s)' },
  { id: 'cleaning_type', label: 'Cleaning Type' },
  { id: 'status', label: 'Status' },
  { id: 'percentage', label: 'Progress' },
  { id: 'cleaned_area', label: 'Actual Cleaned Area (m²)' },
  { id: 'expected_cleaned_area', label: 'Planned Area (m²)' },
  { id: 'estimated_time_required', label: 'Est. Duration' },
  // { id: 'estimated_time_remaining', label: 'Time Remaining' },
  { id: 'start_time', label: 'Start Time' },
  { id: 'completed_time', label: 'Completed Time' },
];

const emptyCleaningResponse: CleaningTaskResponse = {
  count: 0,
  num_pages: 1,
  current_page: 1,
  data: [],
};

const JobPage: React.FC = () => {
  const { isCleaningMode } = useDashboardMode();
  const { profile, accessToken } = useAuth();

  const [page, setPage] = useQueryState("page", parseAsInteger.withDefault(1));
  const [pageSize, setPageSize] = useQueryState("limit", parseAsInteger.withDefault(10));
  const [searchTerm, setSearchTerm] = useQueryState("search", { defaultValue: '' });
  const [selectedStatus, setSelectedStatus] = useQueryState("status", { defaultValue: "all" });
  const [selectedStartLocation, setSelectedStartLocation] = useQueryState("start_location", { defaultValue: "all" });
  const [selectedEndLocation, setSelectedEndLocation] = useQueryState("end_location", { defaultValue: "all" });
  const [selectedRobotName, setSelectedRobotName] = useQueryState("robot", { defaultValue: "all" });

  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>(defaultTime.name);
  const [selectedTimeValue, setSelectedTimeValue] = useState<number>(defaultTime.value.time);
  const [selectedTimeUnit, setSelectedTimeUnit] = useState<dayjs.ManipulateType>(defaultTime.value.unit);
  const [startGraphDate, setStartGraphDate] = useState<Dayjs | null>(null);
  const [endGraphDate, setEndGraphDate] = useState<Dayjs | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<{ label: string; value: string; key: string }[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [reportId, setReportId] = useState<number | string>(0);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const [cleaningTaskData, setCleaningTaskData] = useState<CleaningTaskResponse>(emptyCleaningResponse);
  const [isLoadingCleaning, setIsLoadingCleaning] = useState(false);

  const { isShowAnalytics } = useShowAnalytics();
  const navigate = useNavigate();

  const { data: dataRobot } = useRobots();
  const robots = dataRobot?.data || [];

  const { data: dataLocation } = useLocationPoints();
  const allLocations = dataLocation?.data || [];

  const resolvedOrganisationId = profile?.organisation || localStorage.getItem('organisation_id') || '';

  const resolvedAccessToken =
    accessToken ||
    localStorage.getItem('accessToken') ||
    '';

  const { trigger: submitExport, isLoading: isSubmittingExport } = useTaskReportMutation({
    onSuccess: (data: TaskReportResponse) => {
      setReportId(data.report_id);
      setIsExporting(true);
    }
  });

  const {
    data: dataRobotTask,
    refetch: refetchRobotTask,
    isLoading: isLoadingRobotTask
  } = useRobotTask({
    page,
    page_size: pageSize,
    status: selectedStatus,
    start_time: startDate ? parseInt((startDate.valueOf() / 1000).toString()) : '',
    end_time: endDate ? parseInt((endDate.valueOf() / 1000).toString()) : '',
    start_location: isCleaningMode ? '' : selectedStartLocation,
    end_location: isCleaningMode ? '' : selectedEndLocation,
    robot_name: selectedRobotName,
  });

  const fetchCleaningTasks = useCallback(async () => {
    if (!resolvedOrganisationId) {

      customLog('Cleaning mode active but organisation id is missing', {
        profile,
        resolvedOrganisationId,
      });
      customLog('Setting empty cleaning task data due to missing organisation id');
      setCleaningTaskData(emptyCleaningResponse);
      return;
    }

    try {
      setIsLoadingCleaning(true);
      setError('');

      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('page_size', String(pageSize));

      if (selectedStatus && selectedStatus.toLowerCase() !== 'all') {
        params.append('status', selectedStatus);
      }

      if (startDate) {
        params.append('start_time', String(Math.floor(startDate.valueOf() / 1000)));
      }

      if (endDate) {
        params.append('end_time', String(Math.floor(endDate.valueOf() / 1000)));
      }

      if (selectedRobotName && selectedRobotName.toLowerCase() !== 'all') {
        params.append('robot_id', selectedRobotName);
      }

      const url = `${process.env.REACT_APP_SERVER_URL}/api/v1/organisations/${resolvedOrganisationId}/task/?${params.toString()}`;

      customLog('Fetching cleaning tasks', {
        isCleaningMode,
        resolvedOrganisationId,
        selectedStatus,
        selectedRobotName,
        url,
      });

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${resolvedAccessToken}`,
        },
      });

      customLog('Raw cleaning API response', response.data);

      const payload = response.data;

      const normalized: CleaningTaskResponse = {
        count: Number(payload?.count ?? payload?.data?.count ?? 0),
        num_pages: Number(payload?.num_pages ?? payload?.data?.num_pages ?? 1),
        current_page: Number(payload?.current_page ?? payload?.data?.current_page ?? 1),
        data: Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.results)
            ? payload.results
            : Array.isArray(payload?.data?.data)
              ? payload.data.data
              : [],
      };

      customLog('Normalized cleaning data', normalized);

      setCleaningTaskData(normalized);
    } catch (err: any) {
      console.error(err);
      customLog('Cleaning API error', {
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
      });
      setError(
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to fetch cleaning tasks'
      );
      setCleaningTaskData(emptyCleaningResponse);
    } finally {
      setIsLoadingCleaning(false);
    }
  }, [
    profile,
    resolvedOrganisationId,
    resolvedAccessToken,
    page,
    pageSize,
    selectedStatus,
    startDate,
    endDate,
    selectedRobotName,
    isCleaningMode,
  ]);

  useEffect(() => {
  customLog('Checking access token and profile for JobPage', {
    accessToken,
    profile,
    localStorageOrganisationId: localStorage.getItem('organisation_id'),
  });
}, [profile, accessToken]);


  useEffect(() => {
    customLog('JobPage mode/state', {
      isCleaningMode,
      resolvedOrganisationId,
      profile,
    });

    if (!isCleaningMode) return;
    if (!resolvedOrganisationId) return;

    fetchCleaningTasks();
  }, [isCleaningMode, resolvedOrganisationId, fetchCleaningTasks, profile]);

  useEffect(() => {
    if (isCleaningMode) return;

    refetchRobotTask();
  }, [
    isCleaningMode,
    page,
    pageSize,
    selectedStatus,
    selectedStartLocation,
    selectedEndLocation,
    selectedRobotName,
    startDate,
    endDate,

  ]);

  useEffect(() => {
    if (!selectedTime) return;

    const findData = timeOptions.find(
      (item) => item.name.toLowerCase() === selectedTime.toLowerCase()
    );
    if (!findData) return;

    setSelectedTimeValue(findData.value.time);
    setSelectedTimeUnit(findData.value.unit);
  }, [selectedTime]);

  const activeTaskResponse = isCleaningMode ? cleaningTaskData : dataRobotTask;
  const activeIsLoading = isCleaningMode ? isLoadingCleaning : isLoadingRobotTask;
  const activeColumns = isCleaningMode ? cleaningTasksColumn : deliveryTasksColumn;

  const activeRefetch = () => {
    if (isCleaningMode) {
      fetchCleaningTasks();
    } else {
      refetchRobotTask();
    }
  };

  const validateFilter = () => {
    const newFilters: { label: string; value: string; key: string }[] = [];

    if (selectedStatus !== 'all') {
      newFilters.push({
        label: `Status: ${selectedStatus}`,
        value: selectedStatus,
        key: 'statusFilter'
      });
    }

    if (!isCleaningMode && selectedStartLocation !== 'all') {
      newFilters.push({
        label: `Start Location: ${selectedStartLocation}`,
        value: selectedStartLocation,
        key: 'startLocationFilter'
      });
    }

    if (!isCleaningMode && selectedEndLocation !== 'all') {
      newFilters.push({
        label: `End Location: ${selectedEndLocation}`,
        value: selectedEndLocation,
        key: 'endLocationFilter'
      });
    }

    if (selectedRobotName !== 'all') {
      newFilters.push({
        label: `Robot Name: ${selectedRobotName}`,
        value: selectedRobotName,
        key: 'robotFilter'
      });
    }

    if (startDate || endDate) {
      const formattedStartDate = startDate ? startDate.format('DD/MM/YYYY') : '';
      const formattedEndDate = endDate ? ` - ${endDate.format('DD/MM/YYYY')}` : '';
      newFilters.push({
        label: `Date Range: ${formattedStartDate}${formattedEndDate}`,
        value: '',
        key: 'dateRange'
      });
    }

    setAppliedFilters(newFilters);
  };

  useEffect(() => {
    validateFilter();
  }, [
    isCleaningMode,
    selectedStatus,
    selectedStartLocation,
    selectedEndLocation,
    selectedRobotName,
    startDate,
    endDate,
  ]);

  const mapCleaningTaskRow = (item: CleaningTaskItem) => ({
    ...item,
    id: item.task_id,
    job_number: item.job_number || '-',
    task_name: item.task_name || '-',
    robot_id: item.robot_id || '-',
    areas: Array.isArray(item.areas) ? item.areas.join(', ') : '-',
    cleaning_type: item.cleaning_type || '-',
    status: item.status || '-',
    percentage: `${Math.round((item.percentage || 0) * 100)}%`,
    cleaned_area: item.cleaned_area != null ? Number(item.cleaned_area).toFixed(2) : '-',
    expected_cleaned_area: item.expected_cleaned_area != null ? Number(item.expected_cleaned_area).toFixed(2) : '-',
    estimated_time_required: item.estimated_time_required != null
      ? `${Math.round(item.estimated_time_required)}s`
      : '-',
    estimated_time_remaining: item.estimated_time_remaining != null
      ? `${Math.round(item.estimated_time_remaining)}s`
      : '-',
    // start_time: item.start_time ? dayjs(item.start_time).format('DD/MM/YYYY HH:mm:ss') : '-',
    // completed_time: item.completed_time ? dayjs(item.completed_time).format('DD/MM/YYYY HH:mm:ss') : '-',
    start_time: item.start_time || null,
    completed_time: item.completed_time || null,
  });

  const rawListData = activeTaskResponse?.data || [];

  const tableData = useMemo(() => {
    if (isCleaningMode) {
      return rawListData.map((item: any) => mapCleaningTaskRow(item));
    }
    return rawListData;
  }, [isCleaningMode, rawListData]);

  const fuseOptions = useMemo(() => {
    return isCleaningMode
      ? { keys: ['job_number', 'task_name', 'robot_id', 'areas'], includeScore: true }
      : { keys: ['job_id'], includeScore: true };
  }, [isCleaningMode]);

  const fuse = useMemo(() => {
    return new Fuse(Object.values(tableData), fuseOptions);
  }, [tableData, fuseOptions]);

  const getFilteredData = () => {
    if (!searchTerm) return tableData;
    return fuse.search(searchTerm).map((result) => result.item);
  };

  const filteredData = searchTerm ? getFilteredData() : tableData;

  const totalPage = activeTaskResponse?.num_pages || 1;
  const totalData = activeTaskResponse?.count || 0;

  const getJobList = (currentPage: number, currentPageSize: number) => {
    setPage(currentPage);
    setPageSize(currentPageSize);
  };

  const handleChangeRowsPerPage = (value: number) => {
    setPageSize(value);
    setPage(1);
  };

  const handleStartDateChange = (newStartDate: Dayjs | null) => {
    const today = dayjs();
    if (!newStartDate || !dayjs.isDayjs(newStartDate) || newStartDate.isAfter(today)) {
      setStartDate(null);
      return;
    }
    const startOfDay = dayjs(newStartDate).startOf('day');
    setStartDate(startOfDay);

    if (endDate && endDate.add(6, 'month').startOf('day').isAfter(startOfDay)) {
      setEndDate(null);
    }
  };

  const handleStartGraphDateChange = (newStartDate: Dayjs | null) => {
    const today = dayjs();
    if (!newStartDate || !dayjs.isDayjs(newStartDate) || newStartDate.isAfter(today)) {
      setStartGraphDate(null);
      return;
    }
    const startOfDay = dayjs(newStartDate).startOf('day');
    setStartGraphDate(startOfDay);
  };

  const handleEndGraphDateChange = (newEndDate: Dayjs | null) => {
    const today = dayjs();
    if (!newEndDate || !dayjs.isDayjs(newEndDate) || newEndDate.isAfter(today)) {
      setEndGraphDate(null);
      return;
    }
    const endOfDay = dayjs(newEndDate).endOf('day');
    setEndGraphDate(endOfDay);
  };

  const handleEndDateChange = (newEndDate: Dayjs | null) => {
    const today = dayjs();
    if (!newEndDate || !dayjs.isDayjs(newEndDate) || newEndDate.isAfter(today)) {
      setEndDate(null);
      return;
    }
    const endOfDay = dayjs(newEndDate).endOf('day');
    setEndDate(endOfDay);
  };

  const handleApplyFilters = () => {
    setPage(1);
    setFilterDialogOpen(false);
    activeRefetch();
  };

  const handleResetFilters = () => {
    setPage(1);
    setSearchTerm('');
    setSelectedStatus('all');
    setSelectedStartLocation('all');
    setSelectedEndLocation('all');
    setSelectedRobotName('all');
    setFilterDialogOpen(false);
    setStartDate(null);
    setEndDate(null);
  };

  const handleChangePage = (value: number) => {
    setPage(value > totalPage ? 1 : value);
  };

  const handleExport = (type: string) => {
    const start_time = startDate ? parseInt((startDate.valueOf() / 1000).toString()) : '';
    const end_time = endDate ? parseInt((endDate.valueOf() / 1000).toString()) : '';

    submitExport({
      start_time,
      end_time,
      report_type: type,
      status: selectedStatus?.toLowerCase() === "all" ? "" : selectedStatus,
      start_location: isCleaningMode ? "" : (selectedStartLocation?.toLowerCase() === "all" ? "" : selectedStartLocation),
      robot_name: selectedRobotName?.toLowerCase() === "all" ? "" : selectedRobotName,
    });
  };

  useEffect(() => {
    if (!isExporting || !reportId) return;

    const timer = setTimeout(() => {
      setIsExporting(false);
      setReportId('');
      navigate('/reports');
    }, 8000);

    return () => clearTimeout(timer);
  }, [isExporting, reportId, navigate]);

  const paperRef = useRef<HTMLDivElement | null>(null);

  const exportPDF = () => {
    if (!paperRef.current) {
      console.error('Paper reference is not available');
      return;
    }

    html2canvas(paperRef.current, { scale: 2, logging: true, useCORS: true })
      .then(canvas => {
        customLog('Canvas Size:', canvas.width, canvas.height);
        const imgData = canvas.toDataURL('image/jpeg', 0.3);

        const pdfWidth = 1920;
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

        const pdf = new jsPDF({
          orientation: 'l',
          unit: 'px',
          format: [pdfWidth, pdfHeight],
        });

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`${isCleaningMode ? 'Cleaning' : 'Job'} Analytics ${Date.now().toString()}.pdf`);
      })
      .catch(error => {
        console.error('Error generating PDF:', error);
      });
  };

  const getStartEndTime = (): TimeNumberProps => {
    if (!selectedTime.toLowerCase().includes('custom')) {
      return {
        startTime: parseInt((dayjs().subtract(selectedTimeValue, selectedTimeUnit).startOf('day').valueOf() / 1000).toString()),
        endTime: parseInt((dayjs().endOf('day').valueOf() / 1000).toString()),
      };
    }

    if (!startGraphDate || !endGraphDate) {
      return {
        startTime: parseInt((dayjs().subtract(defaultTime.value.time, defaultTime.value.unit).startOf('day').valueOf() / 1000).toString()),
        endTime: parseInt((dayjs().endOf('day').valueOf() / 1000).toString()),
      };
    }

    return {
      startTime: parseInt((startGraphDate.valueOf() / 1000).toString()),
      endTime: parseInt((endGraphDate.valueOf() / 1000).toString())
    };
  };

  const handleSelectTime = (value: string) => {
    if (!value.toLowerCase().includes('custom')) {
      setStartGraphDate(null);
      setEndGraphDate(null);
    }
    setSelectedTime(value);
  };

  const handleDeleteChip = (key: string) => {
    setAppliedFilters((prevFilters) => prevFilters.filter(filter => filter.key !== key));

    if (key === 'statusFilter') setSelectedStatus('all');
    if (key === 'startLocationFilter') setSelectedStartLocation('all');
    if (key === 'endLocationFilter') setSelectedEndLocation('all');
    if (key === 'dateRange') {
      setStartDate(null);
      setEndDate(null);
    }
    if (key === 'robotFilter') setSelectedRobotName("all");
  };

  const [pageGraph, setPageGraph] = useState(1);
  const [dataPages, setDataPages] = useState<RobotTaskResponse[]>([]);

  const { data: dataListAnalytics, refetch: refetchAnalytics, error: errorAnalytics } = useAnalyticsCard({
    page: 'job',
    enabled: isShowAnalytics
  });

  const cardList = (!!dataListAnalytics && Array.isArray(dataListAnalytics)) ? dataListAnalytics : [];
  const isCardEnabled = (!!dataListAnalytics && Array.isArray(dataListAnalytics));

  const { startTime, endTime } = getStartEndTime();

  const { data: dataRobotTaskGraph, isLoading: isLoadingGraph, refetch: refetchGraph } = useRobotTask({
    page: pageGraph,
    page_size: 100,
    start_time: startTime,
    end_time: endTime,
    start_location: 'all'
  });

  useEffect(() => {
    if (!dataRobotTaskGraph || isLoadingGraph) return;

    const currentPage = dataRobotTaskGraph.current_page;

    if (dataRobotTaskGraph.current_page === 1 || dataPages.length === 0) {
      setDataPages([dataRobotTaskGraph]);
    } else {
      const findPageIndex = dataPages.findIndex((data) => data.current_page === currentPage);
      const tmpData = [...dataPages];

      if (findPageIndex >= 0) {
        tmpData.splice(findPageIndex, 1);
      }

      tmpData.push(dataRobotTaskGraph);
      setDataPages(tmpData);
    }

    if (currentPage < dataRobotTaskGraph.num_pages) {
      setPageGraph(currentPage + 1);
    }
  }, [dataRobotTaskGraph, isLoadingGraph]);

  const current_page = dataRobotTaskGraph?.current_page || 1;
  const dataGraphs = dataPages.map((item) => [...item.data]).flat(1);

  useEffect(() => {
    if (!dataRobotTaskGraph || isLoadingGraph) return;

    if (pageGraph === 1 && current_page === pageGraph) {
      refetchGraph();
    }

    setDataPages([]);
    setPageGraph(1);
  }, [startTime, endTime]);

  useEffect(() => {
    if (!dataRobotTaskGraph || isLoadingGraph) return;
    refetchGraph();
  }, [pageGraph]);

  const cardData: CardType[] = cardList.map((card) => ({
    id: card.uuid,
    x: card.x_position,
    y: card.y_position,
    w: card.width,
    h: card.height,
    minH: card.minHeight,
    option: {
      title: card.title,
      viewType: card.view_type,
      dataType: card.data_type || 'bar_chart',
      filters: card.filters.map((filter) => ({
        value: filter.value,
        filterValue: filter.filterValue
      })),
      is_can_edit_card: card.is_can_edit_card,
    }
  }));

  const [editCardMode, setEditCardMode] = useState(false);
  const [selectedEditCard, setSelectedEditCard] = useState<CardType | undefined>();
  const [wantToDeleteId, setWantToDeleteId] = useState<string | null>(null);
  const [wantToEditId, setWantToEditId] = useState<string | null>(null);

  const { trigger: updateLayouts } = useUpdateAnalyticsLayoutMutation({
    onSuccess: () => {
      refetchAnalytics();
    }
  });

  const { trigger: deleteAnalyticsCard } = useDeleteAnalyticsCardMutation({
    onSuccess: () => {
      refetchAnalytics();
    },
    uuid: wantToDeleteId || '',
  });

  const { trigger: createAnalyticsCard } = useCreateAnalyticsCardMutation({
    onSuccess: () => {
      refetchAnalytics();
    }
  });

  const { trigger: updateCardAnalytic } = useEditAnalyticsCardMutation({
    onSuccess: () => {
      refetchAnalytics();
      setSelectedEditCard(undefined);
    },
    uuid: wantToEditId || '',
  });

  const handleWantToDeleteCard = (uuid: string) => {
    setWantToDeleteId(uuid);
  };

  const [openNewCard, setOpenNewCard] = useState(false);

  const handleOpenNewCard = () => {
    setOpenNewCard(true);
  };

  const handleCloseNewCard = () => {
    setOpenNewCard(false);
  };

  const handleSubmitNewCard = (data: CustomCardProps) => {
    customLog('New Card Data:', data);

    const newY = cardData.length > 0
      ? Math.max(...cardData.map(card => card.y + card.h))
      : 0;

    createAnalyticsCard({
      page_type: "job",
      view_type: data.viewType,
      data_type: data.dataType,
      title: data.title,
      x_position: 0,
      y_position: newY || 0,
      width: 6,
      height: 8,
      filters: data.filters.map((filter) => ({
        value: filter.value,
        filterValue: filter.filterValue
      }))
    });

    setOpenNewCard(false);
  };

  const handleUpdateCardLayout = (layouts: GridLayout.Layout[]) => {
    if (!editCardMode) return;

    updateLayouts({
      layouts: layouts.map((layout) => ({
        uuid: layout.i.substring(0, layout.i.lastIndexOf('-')),
        x_position: layout.x,
        y_position: layout.y,
        width: layout.w,
        height: layout.h,
      }))
    });
  };

  const handleEditCard = (data: CardType) => {
    setSelectedEditCard(data);
    setWantToEditId(data.id);
  };

  const handleCloseEditCard = () => {
    setSelectedEditCard(undefined);
  };

  const handleSubmitEditCard = (data: CustomCardProps) => {
    if (!selectedEditCard) return;

    updateCardAnalytic({
      page_type: "job",
      view_type: data.viewType,
      data_type: data.dataType,
      title: data.title,
      x_position: selectedEditCard.x,
      y_position: selectedEditCard.y,
      width: selectedEditCard.w,
      height: selectedEditCard.h,
      filters: data.filters.map((filter) => ({
        value: filter.value,
        filterValue: filter.filterValue
      }))
    });
  };

  const handleDeleteCard = () => {
    deleteAnalyticsCard();
  };

  const pageTitle = isCleaningMode ? 'Jobs' : 'Jobs';
  const analyticsTitle = isCleaningMode ? 'Cleaning Analytics' : 'Analytic';
  const tableTitle = isCleaningMode ? 'Cleaning Job Tables' : 'Job Tables';
  const exportType = isCleaningMode ? 'simplified_cleaning_job' : 'simplified_job';

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ maxWidth: "95%", m: "15px auto", height: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "15px auto" }}>
          <Typography variant="h4" sx={{ color: "black" }}>
            {pageTitle}
          </Typography>

          {isShowAnalytics && (
            <Button onClick={exportPDF} variant='contained'>
              Export Analytics PDF
            </Button>
          )}
        </Box>

        {/* {isCleaningMode && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              Cleaning mode: {String(isCleaningMode)} | Org ID: {resolvedOrganisationId || 'missing'} | Rows: {cleaningTaskData?.data?.length || 0}
            </Typography>
          </Box>
        )} */}

        {error && (
          <Box sx={{ mb: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {isShowAnalytics && (
          <>
            <Paper ref={paperRef} sx={{ display: "flex", flexDirection: 'column', justifyContent: "right", margin: "15px auto", padding: '16px' }} >
              <Box sx={{ width: '100%', display: "flex", justifyContent: "space-between", alignItems: "center", margin: "8px auto" }}>
                <Typography variant="h5" sx={{ color: "black" }}>Analytic</Typography>
                <Box sx={{ display: "flex", flexDirection: 'row', alignItems: 'center', justifyContent: "right", gap: '12px' }}>
                  {DYNAMIC_WIDGET_ENABLED && isCardEnabled && (
                    <Box sx={{ display: 'flex', flexDirection: 'row', height: '100%', gap: '4px' }}>
                      <Typography sx={{ alignSelf: 'center' }}>Edit Mode</Typography>
                      <Switch checked={editCardMode} onCheckedChange={(val) => setEditCardMode(val)} />
                    </Box>
                  )}
                  <Select
                    size='small'
                    displayEmpty
                    value={selectedTime}
                    onChange={(e) => handleSelectTime(e.target.value)}
                    input={<OutlinedInput />}
                    renderValue={(selected: string) => {
                      if (!selected) {
                        return <em>Placeholder</em>;
                      }

                      return selected
                    }}
                    inputProps={{ 'aria-label': 'Without label' }}
                  >
                    {timeOptions.map((time: TimeOptionType) => (
                      <MenuItem
                        key={time.name}
                        value={time.name}
                        sx={{ textTransform: 'capitalize' }}
                      >
                        {time.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {selectedTime?.toLowerCase().includes('custom') && (
                    <Box sx={{ maxWidth: '350px', display: 'flex', gap: '2px', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <DatePicker
                          label="Start Date"
                          value={startGraphDate}
                          onChange={handleStartGraphDateChange}
                          maxDate={dayjs()}
                          format={DEFAULT_DATEFORMAT}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px' }}>
                        <Typography sx={{ mt: 2, textAlign: 'center' }}>-</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <DatePicker
                          label="End Date"
                          value={endGraphDate}
                          onChange={handleEndGraphDateChange}
                          minDate={startGraphDate || undefined}
                          maxDate={dayjs()}
                          format={DEFAULT_DATEFORMAT}

                        />
                      </Box>
                    </Box>
                  )}
                  {editCardMode && (
                    <Box sx={{ flex: 'none', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
                      <Button onClick={() => handleOpenNewCard()} variant="contained">
                        Add New Card
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
              {/* <Box sx={{ display: "flex", flexDirection: 'column', alignItems: "center", margin: "15px auto" }}> */}
              {DYNAMIC_WIDGET_ENABLED && isCardEnabled && cardData.length > 0 && (
                <ResponsiveGridLayout
                  breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                  cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                  style={{ marginBottom: '15px' }} rowHeight={30} width={1200}
                  onLayoutChange={(layouts) => handleUpdateCardLayout(layouts)}
                  useCSSTransforms={true}
                  isDraggable={editCardMode}
                  isResizable={editCardMode}
                >
                  {cardData.map((card, index) => (
                    <div key={card.id + '-' + index} data-grid={{ x: card.x, y: card.y, w: card.w, h: card.h, minW: 2, minH: card.minH || 8 }}>
                      <CustomCard
                        key={card.id + '-' + index}
                        cardData={card}
                        data={dataGraphs}
                        isLoading={isLoadingGraph}
                        // startTime={startGraphDate}
                        // endTime={endGraphDate}
                        {...getStartEndTime()}
                        onEdit={() => handleEditCard(card)}
                        onDelete={() => handleDeleteCard()}
                        editable={editCardMode}
                        wantToDelete={(id) => handleWantToDeleteCard(id)}
                        cancelDelete={() => setWantToDeleteId(null)}
                      />
                    </div>
                  ))}
                </ResponsiveGridLayout>
              )}
              {DYNAMIC_WIDGET_ENABLED && isCardEnabled && cardData.length === 0 && (
                <Box sx={{ width: '100%', display: "flex", justifyContent: "center", alignItems: "center", margin: "15px auto", padding: '40px 0' }}>
                  <Typography variant="h6" sx={{ textAlign: 'center', fontSize: '14px', color: COLOR.gray[400] }}>
                    No Analytic Card. {editCardMode ? 'Click "Add New Card" to create one.' : 'Enable Edit Mode to add Analytic Cards.'}
                  </Typography>
                </Box>
              )}

              {(!DYNAMIC_WIDGET_ENABLED || !isCardEnabled) && (
                <Grid container spacing={2} sx={{ marginTop: '8px' }}>
                  {/* <SSURate {...getStartEndTime()} data={dataGraphs} isLoading={isLoadingGraph} /> */}
                  <DPRate {...getStartEndTime()} data={dataGraphs} isLoading={isLoadingGraph} />
                  {/* <SSUAverageTime {...getStartEndTime()} data={dataGraphs} isLoading={isLoadingGraph} /> */}
                  <DPAverageTime {...getStartEndTime()} data={dataGraphs} isLoading={isLoadingGraph} />
                  {/* <JobChart time={selectedTimeValue} timeUnit={selectedTimeUnit} /> */}
                  {/* <FailedChart time={selectedTimeValue} timeUnit={selectedTimeUnit} /> */}
                  <LiftSuccessRate {...getStartEndTime()} />
                  <FirstAttemptLiftRate {...getStartEndTime()} />
                  {/* <SSUDeliveryChart {...getStartEndTime()} data={dataGraphs} isLoading={isLoadingGraph} /> */}
                  <DPDeliveryChart {...getStartEndTime()} data={dataGraphs} isLoading={isLoadingGraph} />
                </Grid>
              )}
              {/* </Box> */}
            </Paper>
            <hr style={{ border: `1px solid ${COLOR.gray[200]}`, marginTop: '12px', marginBottom: '12px' }} />

            {DYNAMIC_WIDGET_ENABLED && isCardEnabled && (
              <>
                <NewCustomAnalyticDialog
                  open={openNewCard}
                  handleClose={handleCloseNewCard}
                  handleSubmit={handleSubmitNewCard}
                />

                <NewCustomAnalyticDialog
                  data={selectedEditCard?.option}
                  open={!!selectedEditCard}
                  handleClose={handleCloseEditCard}
                  handleSubmit={handleSubmitEditCard}
                />
              </>
            )}
          </>
        )
        }

        <Typography variant="h6" sx={{ mb: "10px", color: "black" }}>
          {tableTitle}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "10px auto" }}>
          <TextField
            id="search-store-name"
            variant="outlined"
            size="small"
            placeholder={isCleaningMode ? 'Search Cleaning Jobs' : 'Search Jobs'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: "94%",
              background: "white",
              "& .MuiOutlinedInput-root": {
                height: "40px",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <GridSearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <Button
            variant="contained"
            onClick={() => setFilterDialogOpen(true)}
            sx={{
              marginLeft: 2,
              background: "#fff",
              border: "1px solid #c0c0c0",
              boxShadow: "none",
              '&:hover': { background: "#f5f5f5" }
            }}
          >
            <FilterAltIcon sx={{ color: "#c0c0c0" }} />
          </Button>
            {!isCleaningMode && (
              <Button
                disabled={isSubmittingExport}
                variant="contained"
                sx={{ marginLeft: 2 }}
                onClick={() => handleExport(exportType)}
              >
                {isSubmittingExport ? 'Exporting...' : 'Export'}
              </Button>
          )}
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2, textTransform: 'capitalize' }}>
          {appliedFilters.map((filter) => (
            <Chip
              key={filter.key}
              label={underscoreToSpace(filter.label)}
              onDelete={() => handleDeleteChip(filter.key)}
              sx={{
                backgroundColor: "#F1F7FD",
                color: "#378FFE",
                border: "1px solid #CEDBFF",
                textTransform: 'capitalize'
              }}
            />
          ))}
        </Box>

        <JobTable
          columns={activeColumns}
          items={filteredData}
          getJobList={getJobList}
          page={page}
          totalData={totalData}
          rowsPerPage={pageSize}
          isCleaningMode={isCleaningMode}
          isLoading={activeIsLoading}
          handleChangePage={(_, currentPage) => handleChangePage(currentPage + 1)}
          handleChangeRowsPerPage={(e) => handleChangeRowsPerPage(parseInt(e.target.value))}
        />

        <JobFilterDialog
          open={filterDialogOpen}
          onChangeOpen={(value) => setFilterDialogOpen(value)}
          selectedRobotName={selectedRobotName}
          selectedStartLocation={selectedStartLocation}
          selectedEndLocation={selectedEndLocation}
          selectedStartDate={startDate}
          selectedEndDate={endDate}
          selectedStatus={selectedStatus}
          endDateMonthLimit={6}
          hideFilters={isCleaningMode ? ['startLocation', 'endLocation'] : []}
          onChangeFilter={({
            robot,
            status,
            startLocation,
            endLocation,
            startDate: currentStartDate,
            endDate: currentEndDate
          }) => {
            if (robot !== selectedRobotName) setSelectedRobotName(robot || 'all');
            if (status !== selectedStatus) setSelectedStatus(status || 'all');

            if (!isCleaningMode && selectedStartLocation !== startLocation) {
              setSelectedStartLocation(startLocation || 'all');
            }

            if (!isCleaningMode && selectedEndLocation !== endLocation) {
              setSelectedEndLocation(endLocation || 'all');
            }

            if (startDate !== currentStartDate) setStartDate(currentStartDate || null);
            if (endDate !== currentEndDate) setEndDate(currentEndDate || null);
          }}
        />

        <Dialog open={isExporting && !!reportId} onClose={() => setIsExporting(false)}>
          <DialogTitle>Report Generation</DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", alignText: "justify" }} mb={2}>
              <DialogContentText>
                Your file is being generated. Redirecting to the download page in a few seconds...
              </DialogContentText>

              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-around"
                alignItems="flex-start"
                mt={4}
                sx={{ width: "100%", mb: 2 }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: "flex-start" }}>
                  <img src={csv} alt="csv" style={{ height: "50px" }} />
                  <Typography component="div" sx={{ ml: 3, fontSize: "16px", mt: 1.5 }}>
                    Report Reference ID: {reportId}
                  </Typography>
                </Box>
                <CircularProgress sx={{ ml: 2 }} />
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default JobPage;